import React, { useEffect, useState, useRef } from 'react';
import QRCode from "react-qr-code";
import {
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Button,
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Spinner,
  Image,
  useToast,
} from '@chakra-ui/react';
import { useLocation, useHistory } from 'react-router-dom';
import OrderApi from 'api/orders';

export const PaymentErrorBlock = () => {
  return (
    <div>
      <h2>Payment Failed</h2>
      <p>There was an error processing your payment.</p>
    </div>
  );
};

const PayOnline = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const location = useLocation();
  const history = useHistory();
  const query = new URLSearchParams(location.search);
  const dataString = query.get('data');
  const paymentData = dataString
    ? JSON.parse(decodeURIComponent(dataString))
    : null;
  const orderId = paymentData ? paymentData.orderId : null;

  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  const showErrorToast = (msg) => {
    toast({
      title: "Error",
      description: msg,
      status: "error",
      duration: 4000,
      isClosable: true,
      position: "top-right"
    });
  };


  useEffect(() => {
    if (orderId) {
      const fetchData = async () => {
        setLoading(true);

        try {
          let response = await OrderApi.List({ orderId });
          response = response.data;
          if (response.status_code === 200) {
            console.log('Order details fetched', response.data);
            setOrderDetails(response.data);
          } else {
            showErrorToast(response.message || 'Error fetching data');
          }
        } catch (err) {
          showErrorToast(err.message || 'Error fetching data');
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [orderId]);
  
  const handleJuspayPayment = async (orderId) => {
    try {
      setLoading(true);
      let response = await OrderApi.StartTransactionJustpay({ orderId });
      if (response.data?.status_code === 200) {
        setOrderDetails(response.data.data);
        onOpen();
      } else {
        showErrorToast(response.data?.message || 'Error starting Justpay transaction');
      }
    } catch (err) {
      showErrorToast(err.message || 'Error starting Justpay transaction');
    } finally {
      setLoading(false);
    }
  };

  const getJuspayUpiUrl = () => {
    const payload = orderDetails?.details?.payment_gateway_details?.juspayIntent?.payload;
    if (!payload) return null;
    return `upi://pay?pa=${payload.payeeVpa}&pn=${encodeURIComponent(payload.payeeName)}&tr=${payload.merchantRequestId}&am=${payload.amount}&cu=${payload.currency}&mc=${payload.payeeMcc}`;
  };

  return (
    <Flex height='100%' mt={{ base: '140px', md: '120px' }} direction='column'>
      <div>
        {loading ? (
          <Flex justify="center" align="center" mt={10} minH="200px">
            <Spinner size="xl" color="blue.500" thickness="4px" speed="0.65s" />
          </Flex>
        ) : orderDetails ? (
          <Box
            borderWidth='1px'
            borderRadius='md'
            p={4}
            width='100%'
            maxWidth='600px'
          >
            <Table variant='simple'>
              <Thead>
                <Tr>
                  <Th>Detail</Th>
                  <Th>Value</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td>Order ID</Td>
                  <Td>{orderDetails.id}</Td>
                </Tr>
                <Tr>
                  <Td>Amount</Td>
                  <Td>Rs {orderDetails.total_amount || orderDetails.amount}</Td>
                </Tr>
                <Tr>
                  <Td>Order Code</Td>
                  <Td>{orderDetails.code}</Td>
                </Tr>
              </Tbody>
            </Table>

            <Box>
              <Button
                mt={4}
                colorScheme='blue'
                size='lg'
                width='100%'
                variant='solid'
                _hover={{ bg: 'blue.500' }}
                isLoading={loading}
                isDisabled={loading}
                onClick={() => handleJuspayPayment(orderDetails.order_id || orderDetails.id)}
              >
                Pay via Just Pay
              </Button>
            </Box>
            {paymentStatus === 'error' && (
              <Box>
                <PaymentErrorBlock />
              </Box>
            )}
          </Box>
        ) : (
          <p>No payment data available.</p>
        )}
      </div>
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader textAlign="center">Scan to Pay via UPI</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {getJuspayUpiUrl() && (
              <Flex direction="column" align="center" justify="center">
                <Box p={4} bg="white" borderRadius="md">
                  <QRCode
                    size={350}
                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                    value={getJuspayUpiUrl()}
                    viewBox={`0 0 350 350`}
                  />
                </Box>
                <Flex mt="16px" align="center" justify="center" direction="column" w="100%">
                  <p style={{ fontSize: '12px', wordBreak: 'break-all', textAlign: 'center', marginBottom: '8px' }}>
                    {getJuspayUpiUrl()}
                  </p>
                  <Button size="md" colorScheme="blue" w="100%" onClick={() => {
                    navigator.clipboard.writeText(getJuspayUpiUrl());
                    alert('Link copied to clipboard!');
                  }}>
                    Copy URL
                  </Button>
                </Flex>
              </Flex>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default PayOnline;
